import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/requests';
import log from '../../logger';

// Fetch posts with pagination & filters
export const fetchPosts = createAsyncThunk(
  'post/fetchPosts',
  async ({ page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      log.info('Fetching posts with page:', page, 'and limit:', limit);
      const response = await api.posts.getAll({ page, limit, filters });
      if (response.data.success) {
        log.info('Posts fetched successfully:', response.data);
        return response.data;
      } else {
        log.warn('Failed to fetch posts:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching posts:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching posts');
    }
  }
);

// Download TikTok video
export const downloadTikTokVideo = createAsyncThunk(
  'post/downloadTikTokVideo',
  async (videoData, { rejectWithValue }) => {
    try {
      const response = await api.tiktok.downloadVideo(videoData);
      
      if (response.data.success) {
        return response.data;
      } else {
        return rejectWithValue(response.data.error);
      }
    } catch (error) {
      log.error('Error downloading TikTok video:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to download TikTok video');
    }
  }
);

// Create new post
export const createPost = createAsyncThunk(
  'post/createPost',
  async (formData, { dispatch, rejectWithValue }) => {
    try {      
      let postData = formData;
      let useFormData = formData instanceof FormData;
      
      // Handle TikTok videos
      if (formData.videoSource === 'tiktok' && formData.tiktokVideo) {
        // For TikTok videos, first download the video
        const tiktokVideo = formData.tiktokVideo;
        
        try {
          // Download TikTok video first if it has a playUrl (and not already downloaded)
          if (tiktokVideo.playUrl && !formData.videoUrl) {
            const downloadData = await dispatch(downloadTikTokVideo({
              videoUrl: tiktokVideo.playUrl,
              videoMetadata: tiktokVideo
            })).unwrap();
            
            // Update formData with the downloaded video info
            postData.videoUrl = downloadData.videoUrl;
            postData.videoMetadata = JSON.stringify(downloadData.videoMetadata);
          }
        } catch (downloadError) {
          log.error('Error downloading TikTok video:', downloadError);
          return rejectWithValue('Error downloading TikTok video: ' + downloadError);
        }
      } else if (formData.videoSource === 'upload' && formData.video instanceof File) {
        // For uploaded videos, ensure we're using FormData
        useFormData = true;
        
        if (!(postData instanceof FormData)) {
          const formDataObj = new FormData();
          
          // Add the video file
          formDataObj.append('video', formData.video);
          
          // Add other form fields
          Object.keys(formData).forEach(key => {
            if (key !== 'video' && key !== 'tiktokVideo') {
              // Handle arrays and objects by converting them to JSON strings
              if (typeof formData[key] === 'object' && !(formData[key] instanceof File)) {
                formDataObj.append(key, JSON.stringify(formData[key]));
              } else {
                formDataObj.append(key, formData[key]);
              }
            }
          });
          
          // Replace postData with the FormData
          postData = formDataObj;
        }
      }
      
      // Create the post with the appropriate content type
      const response = await api.posts.create(postData, useFormData);
      
      if (response.data.status) {
        return response.data.data;
      } else {
        log.warn('Failed to create post:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error creating post:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to create post');
    }
  }
);

// Update post
export const updatePost = createAsyncThunk(
  'post/updatePost',
  async ({ id, formData }, { dispatch, rejectWithValue }) => {
    try {
      let postData = { ...formData };
      let useFormData = false;
      let formDataObj = null;
      
      // Handle different video sources
      if (formData.videoSource === 'tiktok' && formData.tiktokVideo) {
        // For TikTok videos, first download the video if needed
        const tiktokVideo = formData.tiktokVideo;
        
        try {
          // Download TikTok video if it has a playUrl and we need to update it
          if (tiktokVideo.playUrl && (!formData.videoUrl || formData.updateVideo)) {
            const downloadData = await dispatch(downloadTikTokVideo({
              videoUrl: tiktokVideo.playUrl,
              videoMetadata: tiktokVideo
            })).unwrap();
            
            // Update formData with the downloaded video info
            postData.videoUrl = downloadData.videoUrl;
            postData.videoMetadata = JSON.stringify(downloadData.videoMetadata);
          }
        } catch (downloadError) {
          log.error('Error downloading TikTok video:', downloadError);
          return rejectWithValue('Error downloading TikTok video: ' + downloadError);
        }
      } else if (formData.videoSource === 'upload' && formData.video instanceof File) {
        // For uploaded videos, create a FormData object
        useFormData = true;
        formDataObj = new FormData();
        
        // Add the video file
        formDataObj.append('video', formData.video);
      }
      
      // If using FormData, add all other fields to it
      if (useFormData && formDataObj) {
        // Add other form fields
        Object.keys(postData).forEach(key => {
          if (key !== 'video' && key !== 'tiktokVideo') {
            // Handle arrays and objects by converting them to JSON strings
            if (typeof postData[key] === 'object' && !(postData[key] instanceof File)) {
              formDataObj.append(key, JSON.stringify(postData[key]));
            } else {
              formDataObj.append(key, postData[key]);
            }
          }
        });
        
        // Replace postData with the FormData
        postData = formDataObj;
      }
      
      // Update the post with the appropriate content type
      const response = await api.posts.update(id, postData, useFormData);
      
      if (response.data.status) {
        log.info('Post updated successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to update post:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error updating post:', error);
      return rejectWithValue(error.response?.data?.message || 'Error updating post');
    }
  }
);

// Delete post
export const deletePost = createAsyncThunk(
  'post/deletePost',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Deleting post with ID:', id);
      const response = await api.posts.delete(id);
      if (response.data.status) {
        log.info('Post deleted successfully:', id);
        return id;
      } else {
        log.warn('Failed to delete post:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error deleting post:', error);
      return rejectWithValue(error.response?.data?.message || 'Error deleting post');
    }
  }
);

// Bulk delete posts
export const bulkDeletePosts = createAsyncThunk(
  'post/bulkDeletePosts',
  async (ids, { rejectWithValue }) => {
    try {
      await Promise.all(ids.map(id => api.posts.delete(id)));
      return ids;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error deleting posts');
    }
  }
);

// Get post by ID
export const getPostById = createAsyncThunk(
  'post/getPostById',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Fetching post with ID:', id);
      const response = await api.posts.getById(id);
      if (response.data.status) {
        // Add the fetched post to items array if not exists or update it
        const post = response.data.data;
        log.info('Post fetched successfully:', post);
        return post;
      } else {
        log.warn('Failed to fetch post:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching post:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching post');
    }
  }
);

const postSlice = createSlice({
  name: 'post',
  initialState: {
    items: [],
    total: 0,
    page: 1,
    pages: 0,
    limit: 10,
    loading: false,
    downloadLoading: false,
    error: null,
    downloadError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPosts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPosts.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const { posts, total, page, pages, limit } = action.payload;
        state.items = posts;
        state.total = total;
        state.page = page;
        state.pages = pages;
        state.limit = limit;
      })
      .addCase(fetchPosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(downloadTikTokVideo.pending, (state) => {
        state.downloadLoading = true;
        state.downloadError = null;
      })
      .addCase(downloadTikTokVideo.fulfilled, (state) => {
        state.downloadLoading = false;
        state.downloadError = null;
      })
      .addCase(downloadTikTokVideo.rejected, (state, action) => {
        state.downloadLoading = false;
        state.downloadError = action.payload;
      })
      .addCase(createPost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPost.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items.push(action.payload);
      })
      .addCase(createPost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updatePost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePost.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updatePost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deletePost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePost.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = state.items.filter(item => item._id !== action.payload);
      })
      .addCase(deletePost.rejected, (state) => {
        state.loading = false;
      })
      .addCase(bulkDeletePosts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeletePosts.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(bulkDeletePosts.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getPostById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPostById.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Update or add the post in items array
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        } else {
          state.items.push(action.payload);
        }
      })
      .addCase(getPostById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default postSlice.reducer; 